import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Circle, Square, Loader2, RotateCcw } from "lucide-react";
import { chunkSink, startComposite } from "@/components/podcast/media";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/* Camera + mic recorder. begin() must return { eid, token } for the episode to write to. */
export const CamRecorder = ({ begin, label, onDone, maxMins = 30 }) => {
  const vid = useRef(null);
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const run = useRef(null);
  useEffect(() => {
    let s;
    navigator.mediaDevices?.getUserMedia({ video: { width: 1280, height: 720 }, audio: true }).then(x => { s = x; setStream(x); if (vid.current) vid.current.srcObject = x; })
      .catch(() => setErr("Allow camera and microphone access to record."));
    return () => s?.getTracks().forEach(t => t.stop());
  }, []);
  useEffect(() => { if (state !== "rec") return; const t = setInterval(() => setSecs(x => { if (x + 1 >= maxMins * 60) stop(); return x + 1; }), 1000); return () => clearInterval(t); }); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    try {
      const { eid, token } = await begin();
      const sink = chunkSink(eid, token);
      const c = startComposite({ getVideos: () => [vid.current], getStreams: () => [stream], getLayout: () => "solo", label, onData: sink.push });
      run.current = { c, sink, at: Date.now() };
      setSecs(0); setState("rec");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Couldn't start recording"); }
  };
  const stop = async () => {
    const r = run.current; if (!r) return;
    run.current = null; setState("saving");
    try { await r.c.stop(); await r.sink.finish(Math.round((Date.now() - r.at) / 1000), r.c.mime); setState("done"); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); setState("idle"); }
  };
  return (
    <div className="space-y-3" data-testid="cam-recorder">
      <div className="relative aspect-video bg-slate-950 rounded-2xl overflow-hidden">
        <video ref={vid} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" data-testid="cam-preview" />
        {err && <p className="absolute inset-0 flex items-center justify-center text-white/80 text-sm p-6 text-center" data-testid="cam-error">{err}</p>}
        {state === "rec" && <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 animate-pulse" data-testid="cam-timer"><Circle className="w-2.5 h-2.5 fill-white" />REC {fmt(secs)}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {state === "idle" && <button onClick={start} disabled={!stream} className="h-11 px-6 rounded-full bg-red-600 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-40" data-testid="cam-start"><Circle className="w-4 h-4 fill-white" />Start recording</button>}
        {state === "rec" && <button onClick={stop} className="h-11 px-6 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center gap-2" data-testid="cam-stop"><Square className="w-4 h-4 fill-white" />Stop & save</button>}
        {state === "saving" && <span className="h-11 px-6 flex items-center gap-2 text-sm font-semibold" data-testid="cam-saving"><Loader2 className="w-4 h-4 animate-spin" />Saving your video…</span>}
        {state === "done" && <><span className="h-11 flex items-center text-sm font-bold text-green-700" data-testid="cam-done">Saved!</span><button onClick={() => setState("idle")} className="h-11 px-5 rounded-full border text-sm font-semibold flex items-center gap-1" data-testid="cam-again"><RotateCcw className="w-4 h-4" />Record another</button></>}
        <span className="text-xs text-slate-500 self-center">Up to {maxMins} minutes. Your video uploads while you record.</span>
      </div>
    </div>
  );
};
